<template>
  <div :id="`${source}_${orderType}_booked_members_graph`">
    <ContainerInfoItem
      :source="`${source}_${orderType}_booked_members_graph`"
      :title="
        orderType === bookingOrderTypes.DESC
          ? 'Most Booked Members'
          : 'Least Booked Members'
      "
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <BarChart
        v-else-if="chartCategories && chartData"
        :left-margin="orderType === bookingOrderTypes.DESC ? 6 : 12"
        :id="`${source}_${orderType}_booked_members_chart`"
        :categories="chartCategories"
        :series-data="chartData"
        :alignment="chartAlignment.HORIZONTAL"
      />

      <EmptyScreen
        v-else
        :source="`${source}_${orderType}_booked_members_graph`"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { chartAlignment } from "@/src/constants/chartConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import { bookingOrderTypes } from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import BarChart from "@/src/components/charts/BarChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
  orderType: {
    type: String,
    required: true,
    validator: (value) => Object.values(bookingOrderTypes).includes(value),
  },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);
const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const processBookedMembersData = (data) => {
  if (!data || !data.length) {
    return { categories: null, seriesData: null };
  }

  let processedData = [...data];

  if (props.orderType === bookingOrderTypes.DESC) {
    processedData.sort((a, b) => b.count - a.count);
  } else {
    processedData.sort((a, b) => a.count - b.count);
  }

  processedData = processedData.slice(0, 5);

  const categories = processedData.map((item) => item.user_name);
  const seriesData = [
    {
      name: "Booking Count",
      data: processedData.map((item) => item.count),
      color: props.orderType === bookingOrderTypes.DESC ? "#A146E8" : "#D89435",
    },
  ];

  return { categories, seriesData };
};

const fetchBookedMembers = async () => {
  try {
    isLoading.value = true;

    const params = {
      order: props.orderType,
      action: "created",
      start_time: range.value?.start,
      end_time: range.value?.end,
      user_id: userIds.value,
      team_id: teamIds.value,
      meeting_agent_id: filters.value?.meeting_agent?.id,
    };

    const response = await AnalyticService.V2.fetchBookedMembers(params);
    if (response?.success && response?.data) {
      const data = response?.data?.data;
      const { categories, seriesData } = processBookedMembersData(data);

      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error("An error occurred while fetching booked members: ", error);
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) fetchBookedMembers();
});

useAnalyticsRefresh(fetchBookedMembers, [range, userIds, teamIds]);

onMounted(() => {
  fetchBookedMembers();
});
</script>
